/* eslint-disable react-hooks/exhaustive-deps */
import * as L from 'leaflet';
import { useCallback, useEffect, useState } from "react";
import { 
  getBounds,
  notEqualBounds,
  defaultMapConfig,
  useMapConfig, 
 } from '../../../../components/SubjectsMap/helper';

const locationIcon = '/assets/images/dashboard/scl-icons/location-icon.svg';
const townIcon = '/assets/images/dashboard/scl-icons/town-icon.svg';
const villageIcon = '/assets/images/dashboard/scl-icons/village-icon.svg';
const townDotIcon = '/assets/images/dashboard/scl-icons/town-dot.svg';
const closeIcon = '/assets/images/dashboard/scl-icons/close-icon.svg';

const getCenter = (scl, town) => [
  ((scl.coordinates.lat || 0) + (town.coordinates.lat || 0)) / 2,
  ((scl.coordinates.lng || 0) + (town.coordinates.lng || 0)) / 2,
];

const getPopoverContent = ({ title, data, t = (s) => s }) => `
  <div class="custom-popover">
    <div class="header">
      <h1>${t(title)}</h1>
      <label>${data.name}</label>
    </div>
    <div class="items">
      ${data.locationName ? `
        <div class="item">
          <label>${t('Location')}</label>
          <h5>${data.locationName}</h5>
        </div>
      ` : ``}
      <div class="item">
        <label>${t('Stakeholders Associated')}</label>
        ${data.stakeholdersAssociated ? `<h5 class="green">${t('Yes')}</h5>` : `<h5 class="red">${t('No')}</h5>`}
      </div>
    </div>
  </div>
`;

const getTooltipContent = ({ data, t = (s) => s }) => `
  <div class="custom-tooltip">
    <div class="item">
      <h5 class="main">${t('Distance')}</h5>
      <h5>${data.distance || '-'}</h5>
    </div>
    <div class="item">
      <h5 class="main">${t('Time')}</h5>
      <h5>${data.time || '-'}</h5>
    </div>
  </div>
`;

const useMapHelper = ({
  village,
  scl,
  town,
  filterKey,
  container,
  t,
}) => {
  const { TILE_LAYER_URL, MAP_TOKEN } = useMapConfig();
  const [villageMarker, setVillageMarker] = useState();
  const [villagePopover, setVillagePopover] = useState();
  const [sclMarker, setSclMarker] = useState();
  const [townMarker, setTownMarker] = useState();
  const [townPopover, setTownPopover] = useState();
  const [sclPopover, setSclPopover] = useState();
  const [mapRef, setMapRef] = useState();
  const [townDot, setTownDot] = useState();
  const [townPolyline, setTownPolyline] = useState();
  const [villageDot, setVillageDot] = useState();
  const [vilagePolyline, setVillagePolyilne] = useState();
  const [initDone, setInitDone] = useState(false);
  const [townIsShown, setTownIsShown] = useState(true);
  const [villageIsShown, setVillageIsShown] = useState(true);
  const [townDotTooltip, setTownDotTooltip] = useState();
  const [villageDotTooltip, setVillageDotTooltip] = useState();
  const [lastBounds, setLastBound] = useState(false);

  // Map control handlers
  const zoomHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() + 1);
    }
  }, [mapRef]);

  const zoomOutHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() - 1)
    }
  }, [mapRef]);

  const moveUpHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat + value, center.lng));
    }
  }, [mapRef]);

  const moveDownHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat - value, center.lng));
    }
  }, [mapRef]);

  useEffect(() => {
    if (villageMarker) {
      let icon;
      if (villageIsShown) {
        icon = L.icon({
          iconUrl: villageIcon,
          iconSize: [36, 36],
        });
      } else {
        icon = L.icon({
          iconUrl: closeIcon,
          iconSize: [36, 36],
        });
      }
      villageMarker.setIcon(icon);
    }
  }, [villageIsShown]);

  useEffect(() => {
    if (townMarker) {
      let icon;
      if (townIsShown) {
        icon = L.icon({
          iconUrl: townIcon,
          iconSize: [36, 36],
        });
      } else {
        icon = L.icon({
          iconUrl: closeIcon,
          iconSize: [36, 36],
        });
      }
      townMarker.setIcon(icon);
    }
  }, [townIsShown]);

  const createPopover = ({
    popover,
    data,
    setPopover,
    title,
  }) => {
    const content = getPopoverContent({ data, title, t });
    if (popover) {
      popover.setContent(content);
      return;
    }
    const p = L.popup()
      .setContent(content);
    setPopover(p);
  };

  const createTooltip = ({
    tooltip,
    setTooltip,
    distance,
    time,
  }) => {
    const content = getTooltipContent({ data: { distance, time }, t });
    if (tooltip) {
      tooltip.setContent(content);
    }
    const to = L.popup()
      .setContent(content);
    setTooltip(to);
  };

  useEffect(() => {
    if (mapRef) {
      const bounds = getBounds({ scl, town, village });
      if (bounds.length > 1 && notEqualBounds(bounds, lastBounds)) {
        mapRef.fitBounds(bounds);
        setLastBound(bounds);
        zoomOutHandler();
      } else if (bounds.length === 1) {
        mapRef.setView(bounds[0], 4);
        setLastBound(bounds);
      }
    }
  }, [mapRef, scl, town, village]);

  const createInstance = () => {
    if (container.current) {
      var c = L.DomUtil.get(document.getElementById(container.current));
      if (c != null) {
        c._leaflet_id = null;
      }
      const map = L.map(container.current, {
        ...defaultMapConfig,
        center: [0, 0],
        zoom: 3,
        scrollWheelZoom: false,
      });
      L.control.scale().addTo(map);
      L.tileLayer(TILE_LAYER_URL, { access_token: MAP_TOKEN }).addTo(map);
      return map;
    }
  };

  useEffect(() => {
    if (!mapRef) {
      setMapRef(createInstance());
    }
  }, [scl]);

  useEffect(() => {
    if (townMarker && townPopover) {
      townMarker.bindPopup(townPopover);
    }
  }, [townPopover, townMarker]);

  useEffect(() => {
    if (sclMarker && sclPopover) {
      sclMarker.bindPopup(sclPopover);
    }
  }, [sclMarker, sclPopover]);


  useEffect(() => {
    if (villageDot && villageDotTooltip) {
      villageDot.bindPopup(villageDotTooltip);
      villageDot.removeEventListener('mouseover');
      villageDot.removeEventListener('mouseout');
      villageDot.addEventListener('mouseover', () => {
        villageDot.openPopup();
      });
      villageDot.addEventListener('mouseout', () => {
        villageDot.closePopup();
      });
    }
  }, [villageDot, villageDotTooltip]);

  useEffect(() => {
    if (townDot && townDotTooltip) {
      townDot.bindPopup(townDotTooltip);
      townDot.removeEventListener('mouseover');
      townDot.removeEventListener('mouseout');
      townDot.addEventListener('mouseover', () => {
        townDot.openPopup();
      });
      townDot.addEventListener('mouseout', () => {
        townDot.closePopup();
      });
    }
  }, [townDot, townDotTooltip]);

  useEffect(() => {
    if (villageMarker && villagePopover) {
      villageMarker.bindPopup(villagePopover);
    }
  }, [villageMarker, villagePopover]);

  useEffect(() => {
    if (mapRef && !initDone) {
      let sclM, villageM, townM;
      if (town && town.coordinates) {
        townM = L.marker(town.coordinates, {
          icon: L.icon({
            iconUrl: townIcon,
            iconSize: [36, 36],
            zIndexOffset: 99,
          })
        }).addTo(mapRef);
        createPopover({
          setPopover: (val) => setTownPopover(val),
          popover: townPopover,
          data: town,
          title: 'Main Associated Export Town',
        });
        townM.addEventListener('popupopen', () => setTownIsShown(false));
        townM.addEventListener('popupclose', () => setTownIsShown(true));
      }
      if (scl && Object.keys(scl).length > 0 && scl.coordinates) {
        sclM = L.marker(scl.coordinates, {
          icon: L.icon({
            iconUrl: locationIcon,
            iconSize: [36, 36],
          }),
          zIndexOffset: 100,
        }).addTo(mapRef);
        createPopover({
          setPopover: (val) => setSclPopover(val),
          popover: sclPopover,
          data: scl,
          title: 'Mine Site',
        });
      }
      if (village) {
        villageM = L.marker(village.coordinates, {
          icon: L.icon({
            iconUrl: villageIcon,
            iconSize: [36, 36],
            zIndexOffset: 99,
          })
        }).addTo(mapRef);
        createPopover({
          setPopover: setVillagePopover,
          popover: villagePopover,
          data: village,
          title: 'Associated Village',
        });
        villageM.addEventListener('popupopen', () => setVillageIsShown(false));
        villageM.addEventListener('popupclose', () => setVillageIsShown(true));
      }
      if (town && scl && town.coordinates && scl.coordinates) {
        const center = getCenter(scl, town);
        const polylineM = L.polyline([scl.coordinates, town.coordinates], { color: '#2E90FA', weight: 1 })
          .addTo(mapRef);
        const dotM = L.marker(center, {
          icon: L.icon({
            iconUrl: townDotIcon,
            iconSize: [12, 12],
          })
        })
          .addTo(mapRef);
        createTooltip({
          distance: scl.distanceToExportTown,
          time: scl.accessTimeToExportTown,
          setTooltip: setTownDotTooltip,
          tooltip: townDotTooltip,
        });
        setTownPolyline(polylineM);
        setTownDot(dotM);
      }
      if (village && scl && scl.coordinates && village.coordinates) {
        const center = getCenter(scl, village);
        const polylineM = L.polyline([scl.coordinates, village.coordinates], { color: '#2E90FA', weight: 1 })
          .addTo(mapRef);
        const dotM = L.marker(center, {
          icon: L.icon({
            iconUrl: townDotIcon,
            iconSize: [12, 12],
          })
        })
          .addTo(mapRef);
        createTooltip({
          distance: scl.distanceToMiningVillage,
          time: scl.accessTimeToMiningVillage,
          setTooltip: setVillageDotTooltip,
          tooltip: villageDotTooltip,
        });
        setVillagePolyilne(polylineM);
        setVillageDot(dotM);
      }
      setSclMarker(sclM);
      setVillageMarker(villageM);
      setTownMarker(townM);
      setInitDone(true);
    }
  }, [mapRef]);

  useEffect(() => {
    if (filterKey === '1d' && sclMarker && typeof sclMarker.openPopup === 'function') {
      sclMarker.openPopup();
    }
    if (filterKey === '2d' && villageMarker && typeof villageMarker.openPopup === 'function') {
      villageMarker.openPopup();
    }
  }, [filterKey]);

  const removeMarker = useCallback((ids) => {
    mapRef.eachLayer((layer) => {
      if (ids.includes(layer._leaflet_id)) {
        mapRef.removeLayer(layer);
      }
    });
  }, [mapRef]);

  useEffect(() => {
    if (mapRef) {
      if (scl && Object.keys(scl).length && scl.coordinates) {
        if (sclMarker) {
          sclMarker.setLatLng(scl.coordinates);
          createPopover({
            setPopover: (val) => setSclPopover(val),
            popover: sclPopover,
            data: scl,
            title: 'Mine Site',
          });
        } else {
          const sclM = L.marker(scl.coordinates, {
            icon: L.icon({
              iconUrl: locationIcon,
              iconSize: [36, 36],
            }),
            zIndexOffset: 100,
          }).addTo(mapRef);
          createPopover({
            setPopover: (val) => setSclPopover(val),
            popover: sclPopover,
            data: scl,
            title: 'Mine Site',
          });
          setSclMarker(sclM);
        }
      } else if (sclMarker) {
        removeMarker([sclMarker._leaflet_id]);
        setSclMarker(null);
      }
    }
  }, [scl]);

  useEffect(() => {
    if (mapRef) {
      if (town && town.coordinates) {
        createPopover({
          setPopover: setTownPopover,
          popover: townPopover,
          title: 'Main Associated Export Town',
          data: town,
        });
        if (townMarker) {
          townMarker.setLatLng(town.coordinates);
        } else {
          const townM = L.marker(town.coordinates, {
            icon: L.icon({
              iconUrl: townIcon,
              iconSize: [36, 36],
              zIndexOffset: 99,
            })
          }).addTo(mapRef);
          townM.addEventListener('popupopen', () => setTownIsShown(false));
          townM.addEventListener('popupclose', () => setTownIsShown(true));
          setTownMarker(townM);
        }
      } else if (townMarker) {
        removeMarker([townMarker._leaflet_id]);
        setTownMarker(null);
      }
    }
  }, [town]);

  useEffect(() => {
    if (mapRef) {
      if (village && village.coordinates) {
        createPopover({
          setPopover: setVillagePopover,
          popover: villagePopover,
          data: village,
          title: 'Associated Village',
        });
        if (villageMarker) {
          villageMarker.setLatLng(village.coordinates);
        } else {
          const villageM = L.marker(village.coordinates, {
            icon: L.icon({
              iconUrl: villageIcon,
              iconSize: [36, 36],
              zIndexOffset: 99,
            })
          }).addTo(mapRef);
          villageM.addEventListener('popupopen', () => setVillageIsShown(false));
          villageM.addEventListener('popupclose', () => setVillageIsShown(true));
          setVillageMarker(villageM);
        }
      } else if (villageMarker) {
        removeMarker([villageMarker._leaflet_id]);
        setVillageMarker(null);
      }
    }
  }, [village]);

  useEffect(() => {
    if (mapRef && scl && village && scl.coordinates && village.coordinates) {
      if (villageDot && vilagePolyline) {
        vilagePolyline.setLatLngs([scl.coordinates, village.coordinates]);
        villageDot.setLatLng(getCenter(scl, village));
        createTooltip({
          distance: scl.distanceToMiningVillage,
          time: scl.accessTimeToMiningVillage,
          setTooltip: setVillageDotTooltip,
          tooltip: villageDotTooltip,
        });
      } else {
        const center = getCenter(scl, village);
        const polylineM = L.polyline([scl.coordinates, village.coordinates], { color: '#2E90FA', weight: 1 })
          .addTo(mapRef);
        const dotM = L.marker(center, {
          icon: L.icon({
            iconUrl: townDotIcon,
            iconSize: [12, 12],
          })
        })
          .addTo(mapRef);
        setVillagePolyilne(polylineM);
        setVillageDot(dotM);
        createTooltip({
          distance: scl.distanceToMiningVillage,
          time: scl.accessTimeToMiningVillage,
          setTooltip: setVillageDotTooltip,
          tooltip: villageDotTooltip,
        });
      }
    } else if (villageDot && vilagePolyline) {
      removeMarker([
        villageDot._leaflet_id,
        vilagePolyline._leaflet_id,
        villageDotTooltip._leaflet_id
      ]);
      setVillageDot(null);
      setVillagePolyilne(null);
      setVillageDotTooltip(null);
    }
  }, [scl, village]);

  useEffect(() => {
    if (mapRef && town && scl && town.coordinates && scl.coordinates) {
      if (townDot && townPolyline) {
        townPolyline.setLatLngs([scl.coordinates, town.coordinates]);
        townDot.setLatLng(getCenter(scl, town));
        createTooltip({
          distance: scl.distanceToExportTown,
          time: scl.accessTimeToExportTown,
          setTooltip: setTownDotTooltip,
          tooltip: townDotTooltip,
        });
      } else {
        const center = getCenter(scl, town);
        const polylineM = L.polyline([scl.coordinates, town.coordinates], { color: '#2E90FA', weight: 1 })
          .addTo(mapRef);
        const dotM = L.marker(center, {
          icon: L.icon({
            iconUrl: townDotIcon,
            iconSize: [12, 12],
          })
        })
          .addTo(mapRef);
        setTownPolyline(polylineM);
        setTownDot(dotM);
        createTooltip({
          distance: scl.distanceToExportTown,
          time: scl.accessTimeToExportTown,
          setTooltip: setTownDotTooltip,
          tooltip: townDotTooltip,
        });
      }
    } else if (townDot && townPolyline) {
      removeMarker([
        townDot._leaflet_id,
        townPolyline._leaflet_id,
        townDotTooltip._leaflet_id
      ]);
      setTownDot(null);
      setTownPolyline(null);
      setTownDotTooltip(null);
    }
  }, [scl, town]);

  return {
    zoomHandler,
    zoomOutHandler,
    moveDownHandler,
    moveUpHandler,
  };
};

export default useMapHelper;
